// ImpactAnalysisPanel — v2 full-screen causation explorer. One node at the
// center; upstream (what causes it) flows in from the left, downstream (what
// it triggers) fans out to the right; declared effects (crossings, policies,
// sagas) get typed cards. Every relationship rendered is declared in
// Moment's impact-analysis artifact — Facet renders causation, it never
// guesses.

import { useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setAppView } from '../../store/slices/uiSlice.ts';
import { getCachedImpactAnalysis } from '../../services/ArtifactLoaderService.ts';
import type { ImpactNode } from '../../types/moment-artifacts.ts';

const TYPE_ICONS: Record<string, string> = {
  command: '▸',
  event: '◆',
  policy: '⊕',
  saga: '↻',
  crossing: '⇄',
};

const TYPE_COLORS: Record<string, string> = {
  command: '#63b3ed',
  event: '#c084fc',
  policy: '#f6ad55',
  saga: '#68d391',
  crossing: '#fc8181',
};

const EFFECT_HEADINGS: Record<string, string> = {
  crossing: '⤷ BOUNDARY CROSSING',
  policy: '⚡ POLICY CHAIN',
  saga: '⟳ SAGA',
};

function nodeKey(node: ImpactNode): string {
  return `${node.context}::${node.name}`;
}

function findCascadeDepth(
  key: string,
  nodeMap: Map<string, ImpactNode>,
  visited = new Set<string>(),
): number {
  if (visited.has(key)) return 0;
  visited.add(key);
  const node = nodeMap.get(key);
  if (!node || node.triggers.length === 0) return 0;
  let max = 0;
  for (const t of node.triggers) {
    for (const [k, n] of nodeMap) {
      if (n.name === t) {
        max = Math.max(max, 1 + findCascadeDepth(k, nodeMap, visited));
      }
    }
  }
  return max;
}

export default function ImpactAnalysisPanel() {
  const dispatch = useDispatch();
  const [selectedNode, setSelectedNode] = useState<string | null>(null);

  const analysis = getCachedImpactAnalysis();

  const nodeMap = useMemo(() => {
    const m = new Map<string, ImpactNode>();
    if (analysis) for (const node of analysis.nodes) m.set(nodeKey(node), node);
    return m;
  }, [analysis]);

  const contexts = useMemo(() => {
    if (!analysis) return [];
    return [...new Set(analysis.nodes.map((n) => n.context))].sort();
  }, [analysis]);

  const criticalNodes = useMemo(() => {
    if (!analysis) return [];
    return analysis.nodes
      .map((n) => ({ key: nodeKey(n), name: n.name, depth: findCascadeDepth(nodeKey(n), nodeMap) }))
      .filter((n) => n.depth > 1)
      .sort((a, b) => b.depth - a.depth)
      .slice(0, 8);
  }, [analysis, nodeMap]);

  // Default: the deepest-cascade node, so the screen answers immediately.
  const effectiveKey = selectedNode ?? criticalNodes[0]?.key ?? null;
  const selected = effectiveKey ? (nodeMap.get(effectiveKey) ?? null) : null;

  const byName = useCallback(
    (name: string): { node: ImpactNode; key: string } | null => {
      for (const [k, n] of nodeMap) if (n.name === name) return { node: n, key: k };
      return null;
    },
    [nodeMap],
  );

  // Upstream: declared dependencies + any node that declares it triggers this one.
  const upstream = useMemo(() => {
    if (!selected || !analysis) return [];
    const seen = new Set<string>();
    const list: { name: string; node: ImpactNode | null; key: string | null }[] = [];
    for (const dep of selected.dependsOn) {
      const r = byName(dep);
      if (!seen.has(dep)) {
        seen.add(dep);
        list.push({ name: dep, node: r?.node ?? null, key: r?.key ?? null });
      }
    }
    for (const n of analysis.nodes) {
      if (n.triggers.includes(selected.name) && !seen.has(n.name)) {
        seen.add(n.name);
        list.push({ name: n.name, node: n, key: nodeKey(n) });
      }
    }
    return list;
  }, [selected, analysis, byName]);

  const downstream = useMemo(() => {
    if (!selected) return [];
    return selected.triggers.map((t) => {
      const r = byName(t);
      return { name: t, node: r?.node ?? null, key: r?.key ?? null };
    });
  }, [selected, byName]);

  const effects = downstream.filter(
    (d) =>
      d.node && (d.node.type === 'crossing' || d.node.type === 'policy' || d.node.type === 'saga'),
  );

  if (!analysis) {
    return (
      <div className="fv2-page" data-screen="impact">
        <div className="fv2-page-inner">
          <div className="fv2-breadcrumb">
            <button className="fv2-crumb-link" onClick={() => dispatch(setAppView('portfolio'))}>
              Ledger
            </button>
            <span className="fv2-crumb-sep">/</span>
            Impact analysis
          </div>
          <h2 className="fv2-flow-title">Impact analysis</h2>
          <div className="fv2-diff-empty">
            No impact analysis data available. Load a Moment-generated manifest with an
            impact-analysis.json artifact.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fv2-page" data-screen="impact">
      <div className="fv2-page-inner">
        <div className="fv2-breadcrumb">
          <button className="fv2-crumb-link" onClick={() => dispatch(setAppView('portfolio'))}>
            Ledger
          </button>
          <span className="fv2-crumb-sep">/</span>
          Impact analysis
        </div>
        <h2 className="fv2-flow-title">
          Impact analysis <span className="fv2-flow-title-sub">CAUSATION · BOTH DIRECTIONS</span>
        </h2>

        <div className="fv2-impact-picker">
          <span className="fv2-colhead">NODE</span>
          <select
            className="fv2-select"
            value={effectiveKey ?? ''}
            onChange={(e) => setSelectedNode(e.target.value || null)}
            aria-label="Impact node"
          >
            {contexts.map((ctx) => (
              <optgroup key={ctx} label={ctx}>
                {analysis.nodes
                  .filter((n) => n.context === ctx)
                  .map((n) => (
                    <option key={nodeKey(n)} value={nodeKey(n)}>
                      {TYPE_ICONS[n.type]} {n.name}
                    </option>
                  ))}
              </optgroup>
            ))}
          </select>
          <span className="fv2-impact-note">
            every relationship shown is declared in the spec — Facet renders causation, it never
            guesses
          </span>
        </div>

        {criticalNodes.length > 0 && (
          <div className="fv2-impact-critical">
            <span className="fv2-colhead">DEEPEST CASCADES</span>
            {criticalNodes.map((cn) => (
              <button
                key={cn.key}
                className={`fv2-impact-crit-btn${effectiveKey === cn.key ? ' fv2-nav-on' : ''}`}
                onClick={() => setSelectedNode(cn.key)}
              >
                {cn.name} <span className="fv2-dim">{cn.depth} deep</span>
              </button>
            ))}
          </div>
        )}

        {selected && (
          <>
            <div className="fv2-impact-grid">
              <div className="fv2-impact-col fv2-impact-up">
                <div className="fv2-colhead" style={{ textAlign: 'right' }}>
                  ⟵ UPSTREAM · WHAT CAUSES IT
                </div>
                {upstream.length === 0 && (
                  <div className="fv2-dim" style={{ textAlign: 'right' }}>
                    nothing declared upstream — a root node
                  </div>
                )}
                {upstream.map((u) => (
                  <button
                    key={u.name}
                    className="fv2-impact-node"
                    style={{ textAlign: 'right' }}
                    disabled={!u.key}
                    onClick={() => u.key && setSelectedNode(u.key)}
                  >
                    <span className="fv2-impact-node-name">
                      {u.node && (
                        <span style={{ color: TYPE_COLORS[u.node.type] }}>
                          {TYPE_ICONS[u.node.type]}{' '}
                        </span>
                      )}
                      {u.name}
                    </span>
                    {u.node && <span className="fv2-impact-node-where">{u.node.context}</span>}
                  </button>
                ))}
                <div className="fv2-impact-caption" style={{ textAlign: 'right' }}>
                  declared dependencies + declared triggers-of — replayed, not inferred
                </div>
              </div>

              <div className="fv2-impact-center">
                <div className="fv2-kicker">THE {selected.type.toUpperCase()}</div>
                <div className="fv2-impact-center-name">
                  <span style={{ color: TYPE_COLORS[selected.type] }}>
                    {TYPE_ICONS[selected.type]}
                  </span>{' '}
                  {selected.name}
                </div>
                <div className="fv2-impact-center-where">{selected.context}</div>
              </div>

              <div className="fv2-impact-col">
                <div className="fv2-colhead">DOWNSTREAM · WHAT IT TRIGGERS ⟶</div>
                {downstream.length === 0 && (
                  <div className="fv2-dim">no downstream effects — a leaf node</div>
                )}
                {downstream.map((d) => (
                  <button
                    key={d.name}
                    className="fv2-impact-node"
                    disabled={!d.key}
                    onClick={() => d.key && setSelectedNode(d.key)}
                  >
                    <span className="fv2-impact-node-name">
                      {d.node && (
                        <span style={{ color: TYPE_COLORS[d.node.type] }}>
                          {TYPE_ICONS[d.node.type]}{' '}
                        </span>
                      )}
                      {d.name}
                    </span>
                    {d.node && <span className="fv2-impact-node-where">{d.node.context}</span>}
                  </button>
                ))}
              </div>
            </div>

            {effects.length > 0 && (
              <div className="fv2-impact-effects">
                <div className="fv2-colhead">DECLARED EFFECTS AT THIS NODE</div>
                <div className="fv2-impact-effects-grid">
                  {effects.map((fx) => {
                    const t = fx.node!.type;
                    return (
                      <div
                        className="fv2-effect-card"
                        key={fx.name}
                        style={{ borderColor: TYPE_COLORS[t] }}
                      >
                        <div className="fv2-effect-head" style={{ color: TYPE_COLORS[t] }}>
                          {EFFECT_HEADINGS[t] ?? t.toUpperCase()} · {fx.node!.context.toUpperCase()}
                        </div>
                        <div className="fv2-effect-body">
                          {selected.name} triggers <b>{fx.name}</b> — declared in the spec as a{' '}
                          {t === 'crossing'
                            ? 'boundary crossing into another context'
                            : t === 'policy'
                              ? 'reactive policy chain'
                              : 'saga interaction'}
                          .
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
